/**
 * The thread under a public document.
 *
 * One thread, two sides: what the client writes here and what an operator
 * writes in the CRM are the same list, in one order. A client asking "can we
 * drop the second item?" gets answered under the offer they asked about,
 * instead of in a mail nobody can link back to it three weeks later.
 *
 * Everything written here is visible to everyone holding the link. That is said
 * on screen rather than assumed, because the box sits under a document the
 * client may have forwarded to their own accountant.
 */
import { useState } from "react";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { Button, Card, CardContent, Input, Label, Textarea } from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { fmtDateTime } from "../../lib/format";
import { publicDoc, type PublicDocComment } from "../../lib/publicDoc";

export function DocComments({
  token,
  comments,
  onAdded,
}: {
  token: string;
  comments: PublicDocComment[];
  onAdded: (c: PublicDocComment) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || !body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await publicDoc.comment(token, {
        name: name.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
        body: body.trim(),
      });
      onAdded(res.comment);
      setBody("");
    } catch {
      setError(t("publicDoc.comments.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
          <MessageSquare className="size-4 text-muted-foreground" />
          {t("publicDoc.comments.title")}
        </h2>

        {comments.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">{t("publicDoc.comments.empty")}</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {comments.map((c) => (
              <li
                key={c.id}
                className={`rounded-xl border border-border p-3 ${c.authorKind === "staff" ? "bg-muted/40" : ""}`}
              >
                <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{c.authorName}</span>
                  <span>{c.authorKind === "staff" ? t("publicDoc.comments.fromUs") : t("publicDoc.comments.fromClient")}</span>
                  <span>{fmtDateTime(c.createdAt, locale)}</span>
                </div>
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-foreground">{c.body}</p>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5 space-y-4 border-t border-border pt-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="pc-name">{t("publicDoc.field.name")}</Label>
              <Input id="pc-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </div>
            <div>
              <Label htmlFor="pc-email">{t("publicDoc.field.email")}</Label>
              <Input id="pc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </div>
          </div>
          <div>
            <Label htmlFor="pc-body">{t("publicDoc.comments.yours")}</Label>
            <Textarea id="pc-body" rows={3} value={body} onChange={(e) => setBody(e.target.value)} />
            <p className="mt-1 text-xs text-muted-foreground">{t("publicDoc.comments.visibility")}</p>
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <Button onClick={submit} disabled={busy || !name.trim() || !body.trim()}>
            {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Send className="mr-2 size-4" />}
            {t("publicDoc.comments.send")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
